import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {type ChangeEvent, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {isExternalOrderNumberFormat, isRevaIdFormat} from "@reva/shared/value-order";

import {IconFieldTextarea} from "../../component/iconFieldChildren";
import {useQueryParam} from "../../hooks/navigationHooks";
import {useCreatablePurposes, useIsRevisionReadOnly} from "../../hooks/permissionHooks";
import {useDebouncedValue} from "../../hooks/useDebouncedValue";
import {
    removeQueryError,
    selectAddressAmbiguousData,
    selectAddressSelected,
    selectError,
    selectLoading,
} from "../../redux/addressSlice";
import {searchAddressAsync} from "../../redux/addressThunk";
import {selectImmodropLoading, selectAddressLoading as selectImportAddressLoading} from "../../redux/importSlice";
import {finalizeImport, queryImmodropAsync, queryImmodropFileAsync, queryJsonInputAsync} from "../../redux/importThunk";
import {
    removePersistenceQueryError,
    selectPersistenceInitialized,
    selectPersistenceLoading,
    selectPersistenceQueryError,
    selectRevaId,
} from "../../redux/persistenceSlice";
import {fetchPersistence, initializePersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {setFreeTextThunk} from "../../redux/searchValuationsThunk";
import {isHttpUrl} from "../../util/browserUtil";
import {handlePreventDefault} from "../../util/events";
import {DialogImmodropImport, DialogJsonImport} from "../DialogImport";
import AmbiguousAddressDialog from "./AmbiguousAddressDialog";
import DuplicateValuationDialog from "./DuplicateValuationDialog";
import PurposeChoiceDialog from "./PurposeChoiceDialog";

interface AutoDetectMultiInputProps {
    className: string;
}

export default function AutoDetectMultiInput({className}: AutoDetectMultiInputProps) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const addressInputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Der PDF-Upload legt eine neue REVA-ID an. Revision-Readonly-User und User ohne Zweck-Recht (#19481) dürfen
    // keine REVA-IDs anlegen, daher sehen sie den Button nicht. Die Suche bleibt, sie öffnet bestehende IDs.
    const isRevisionReadOnly = useIsRevisionReadOnly();
    const mayCreate = useCreatablePurposes().length > 0;

    const addressSelected = useRevaSelector(selectAddressSelected);
    const ambiguousAddresses = useRevaSelector(selectAddressAmbiguousData);
    const immodropLoading = useRevaSelector(selectImmodropLoading);
    const importAddressLoading = useRevaSelector(selectImportAddressLoading);
    const addressLoading = useRevaSelector(selectLoading);
    const persistenceLoading = useRevaSelector(selectPersistenceLoading);
    const persistenceInitialized = useRevaSelector(selectPersistenceInitialized);
    const revaId = useRevaSelector(selectRevaId);
    const queryError = useRevaSelector(selectError);
    const persistenceQueryError = useRevaSelector(selectPersistenceQueryError);

    const hasAmbiguousAddresses = !!ambiguousAddresses;
    const hasError = !!queryError || !!persistenceQueryError;

    // #18908: one-shot ?search=<input> handling (see effect below)
    const [searchQueryParam, setSearchQueryParam] = useQueryParam("search");
    const searchParamConsumed = useRef(false);

    // Address-related state
    const [addressQuery, setAddressQuery] = useState("");

    // Dialog visibility state
    const [immodropDialogVisible, setImmodropDialogVisible] = useState(false);
    const [jsonDialogVisible, setJsonDialogVisible] = useState(false);

    const loading = addressLoading || persistenceLoading || immodropLoading || importAddressLoading;

    const onEnterAddress = async (input: string) => {
        dispatch(removeQueryError());
        dispatch(removePersistenceQueryError());

        if (loading || addressSelected) return;

        const inputClean = input.trim();

        try {
            if (inputClean.match(/^\s*\{.*}\s*$/s)) {
                const result = await dispatch(queryJsonInputAsync(inputClean));
                if (!result) {
                    setJsonDialogVisible(true);
                } else {
                    void dispatch(finalizeImport());
                }
            } else if (isHttpUrl(inputClean)) {
                await dispatch(queryImmodropAsync(inputClean));

                setImmodropDialogVisible(true);
            } else if (
                // REVA-ID (also covers a snapshot id "REVA-….<n>" or "REVA-…:<n>", split downstream in fetchPersistenceByIdInternal)
                isRevaIdFormat(inputClean) ||
                // Externe Order-Nummer
                isExternalOrderNumberFormat(inputClean) ||
                // Value Order-GUID
                inputClean.match(/^[0-da-f]{8}-[0-da-f]{4}-[0-da-f]{4}-[0-da-f]{4}-[0-da-f]{12}$/) ||
                // Europace Objektwert-Referenz
                inputClean.match(/^v\d:[0-da-f]{64}.*/) ||
                // Immobilien-ID
                inputClean.match(/^[0-da-f]{24}/)
            ) {
                await dispatch(fetchPersistence({id: inputClean}));
            } else {
                const addressFound = await dispatch(searchAddressAsync(inputClean));
                if (addressFound) {
                    // #18907: check for existing valuations at this address first; opens a selection
                    // dialog on hits, otherwise creates the new valuation directly (the duplicate check
                    // is the default behavior of initializePersistence).
                    await dispatch(initializePersistence(addressFound));
                }
            }
        } catch (e) {
            console.error("Error processing address query:", e);
            setJsonDialogVisible(true);
        }
    };
    const onEnterAddressEventHandler = handlePreventDefault(() => void onEnterAddress(addressQuery));

    // Live-Freitextfilter der Startseiten-Listen (zusätzlich zum Enter/Button-Verhalten, das UNVERÄNDERT
    // die Bewertung lädt/anlegt): die Eingabe wird ~800 ms entprellt und dann an setFreeTextThunk gegeben,
    // der ab 3 Zeichen filtert und darunter (auch beim Leeren) den Filter zurücksetzt. Beim Verlassen der
    // Startseite (Unmount) wird der Filter zurückgesetzt, damit keine stale Freitext-Zahl zurückbleibt.
    const debouncedQuery = useDebouncedValue(addressQuery, 800);
    useEffect(() => {
        void dispatch(setFreeTextThunk(debouncedQuery));
    }, [dispatch, debouncedQuery]);
    useEffect(
        () => () => {
            void dispatch(setFreeTextThunk(null));
        },
        [dispatch],
    );

    // #18908: When REVA is opened with ?search=<input> and no valuation is loaded yet, take the
    // parameter into the multi-input once and submit it just like a manual search. Fires exactly
    // once (guarded by searchParamConsumed), then never again.
    useEffect(() => {
        if (searchParamConsumed.current) return;
        if (!persistenceInitialized || persistenceLoading || revaId) return;

        const search = searchQueryParam?.trim();
        if (!search) return;

        searchParamConsumed.current = true; // only once, never again
        setAddressQuery(search); // show it in the input
        void onEnterAddress(search); // submit the search (explicit value -> no stale closure)
        setSearchQueryParam(null); // clear the param afterwards (replace nav, other params kept)
        // onEnterAddress ist absichtlich KEINE Dependency: als bei jedem Render neue Closure würde sie
        // den Einmal-Effect zu einem bei-jedem-Render-Effect machen; aufgerufen wird sie ohnehin mit
        // explizitem Wert (kein stale-closure-Risiko für `search`).
    }, [persistenceInitialized, persistenceLoading, revaId, searchQueryParam, setSearchQueryParam]);

    const onPdfFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";
        await dispatch(queryImmodropFileAsync(file));
        setImmodropDialogVisible(true);
    };

    return (
        <>
            <input
                type="file"
                accept=".pdf"
                ref={fileInputRef}
                style={{display: "none"}}
                onChange={(e) => void onPdfFileSelected(e)}
            />
            <form className={`${className} flex items-start`} onSubmit={onEnterAddressEventHandler}>
                <IconField iconPosition="left" className="flex-1">
                    <InputIcon className="pi pi-search" />
                    <IconFieldTextarea
                        id="address"
                        ref={addressInputRef}
                        placeholder={t("address.placeholder")}
                        invalid={hasError || hasAmbiguousAddresses}
                        className="w-full"
                        value={addressQuery}
                        disabled={loading}
                        autoResize
                        rows={1}
                        onChange={(e) => {
                            setAddressQuery(e.target.value);
                            // Fehler-Banner einer vorherigen (z.B. versehentlichen) Enter-Suche
                            // verschwinden, sobald weitergetippt wird.
                            if (queryError) {
                                dispatch(removeQueryError());
                            }
                            if (persistenceQueryError) {
                                dispatch(removePersistenceQueryError());
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                void onEnterAddress(addressQuery);
                            }
                        }}
                    />
                </IconField>
                {!isRevisionReadOnly && mayCreate && (
                    <Button
                        id="pdfUploadButton"
                        icon="pi pi-file-pdf"
                        tooltip={t("address.uploadPdf")}
                        tooltipOptions={{position: "top"}}
                        className="ml-4"
                        loading={loading}
                        disabled={loading}
                        onClick={() => fileInputRef.current?.click()}
                        type="button"
                    />
                )}
                <Button
                    id="addressQueryButton"
                    icon="pi pi-arrow-circle-right"
                    label={t("address.query")}
                    className="ml-4"
                    loading={loading}
                    onClick={onEnterAddressEventHandler}
                />
            </form>
            <DialogJsonImport
                visible={jsonDialogVisible}
                setVisible={setJsonDialogVisible}
                initialText={addressQuery.trim()}
            />
            <DialogImmodropImport
                visible={immodropDialogVisible}
                setVisible={setImmodropDialogVisible}
                initialText={addressQuery.trim()}
            />
            <AmbiguousAddressDialog
                visible={hasAmbiguousAddresses && !persistenceLoading}
                addressQuery={addressQuery.trim()}
                addressInputRef={addressInputRef}
            />
            <DuplicateValuationDialog addressInputRef={addressInputRef} />
            <PurposeChoiceDialog pending="address" addressInputRef={addressInputRef} />
        </>
    );
}
